/**
 * ScrollArea — a bounded, independently-scrolling region.
 *
 * Formalises the idiom already hand-rolled across the SPA for long lists inside a
 * card (`max-h-* overflow-y-auto pr-1`): the page keeps scrolling as one document
 * — the house convention — while a list that would otherwise run to thousands of
 * pixels stays inside its own box.
 *
 * `pr-1` keeps the scrollbar off the content; `overscroll-contain` stops a wheel
 * gesture chaining to the page when the list hits an end.
 *
 * Deliberately NOT focusable. The rows inside these lists are already buttons /
 * checkboxes, so the region is keyboard-reachable through them and Tab
 * auto-scrolls it — while a `tabIndex={0}` here would trip
 * `jsx-a11y/no-noninteractive-tabindex`, which is an ERROR in this repo's config.
 * If you ever add a ScrollArea whose content has no focusable children, give it a
 * `role`/`tabIndex` at THAT call site with an eslint-disable and a reason.
 */

import type { ReactNode } from "react";
import { cn } from "./cn";

export interface ScrollAreaProps {
  children: ReactNode;
  /**
   * Tailwind max-height class(es) bounding the region — e.g. `"max-h-64"` or
   * `"lg:max-h-[60vh]"`. Breakpoint-prefix it to leave small screens in normal
   * document flow, which is what keeps mobile free of nested scrollers.
   */
  maxH: string;
  /** Names the region for assistive tech. Pairs with `role="group"`. */
  label?: string;
  className?: string;
  /** Product-tour anchor, when the scroller itself is the highlighted element. */
  "data-tour"?: string;
}

export function ScrollArea({
  children,
  maxH,
  label,
  className,
  "data-tour": dataTour,
}: ScrollAreaProps) {
  return (
    <div
      role={label ? "group" : undefined}
      aria-label={label}
      data-tour={dataTour}
      className={cn("overflow-y-auto overscroll-contain pr-1", maxH, className)}
    >
      {children}
    </div>
  );
}
